/* eslint-disable */
import React, { FC, useState, useEffect } from 'react';
import { connect, useSelector } from 'react-redux';
// eslint-disable-next-line import/no-unresolved
import { setSharedValueState } from '@store/formSharedValues/actions';
import { Input, DateInput } from '../../components/inputs/index';
import SelectBox from '../../components/selectBox/selectBox';
import { FormItem, FieldError } from '../../components/helpers';
import RadioButtons from '../../components/radioButtons';
import Label from '../../components/Label';
import { ButtonList, CheckButton } from '../../components/buttons';
import AutoComplete from '../../components/autoComplete/autoComplete';
import { validateField } from '../../utils/inputValidations';
import { AsyncAutoComplete } from '../asyncAutoComplete';
import { PillList } from '../pillList';
import { PersonalizationToggle } from '../../components/pillList';
import InputAIDisclaimer from '../../components/inputAIDisclaimer';
import getAutoCompleteKey from '../../utils/browserAutoCompleteMapping';
import { displayHeaderCartoon } from '@src/utils/uiOverrides';
import FORM_STATE from '@src/constants/formStateConstants';
import { IRootState } from '@store/root/rootState';

interface Props {
  stepInputState: any;
  index: number;
  width?: number;
  fieldData: any;
  onChange?: (e: any) => void;
  collectValues?: () => void;
  value?: (e: any) => void;
  error?: boolean;
  errorState?: any;
  currentStep: number;
  formSharedValues?: any;
  compressHeight?: boolean;
  setSharedValue?: (stepValues: Array<object>) => void;
  locId: number;
  personalizationPillsShown?: boolean;
  handlePersonalizationShown?: (input: boolean) => void;
  personalizationCompleted: boolean;
  currentStepId?: number;
}

type FieldData = {
  value: string;
  hasError: boolean;
  error: boolean;
  fieldValue: string;
  defaultValue: string;
  label: string;
  fieldId: string;
  inputType: string;
  disabled: boolean;
  required: boolean;
  errorMessage: string | null;
  sortOrder: string;
  compressHeight?: boolean;
  list: [];
  placeholder: string;
  __typename: string;
};

const getDefaultValueForField = (field: FieldData): any => {
  switch (field.inputType) {
    case 'radiolist':
    case 'radio':
      // @ts-ignore
      return field.list && field.list.length > 0 ? field.list[0].value : '';
    case 'list':
    case 'autocomplete':
      return field.defaultValue ? JSON.parse(field.defaultValue) : '';
    case 'select':
      return field.defaultValue ? JSON.parse(field.defaultValue) : '';
    default:
      return field.defaultValue;
  }
};

const mapToNumberInput = ['gpa', 'sat', 'act'];

const validateInput = (inputValue: string) => {
  let validatedInput = inputValue;
  // The linter does not like the escape sequences in this regex.
  const allowedChars = /[^a-z0-9~`!@#$%^&*()\-_+={}\[\]\|\\/:;"'<>,.?\s]/gi; // eslint-disable-line
  if (allowedChars.test(validatedInput)) {
    validatedInput = validatedInput.replace(allowedChars, '');
  }
  return validatedInput;
};

const numericValidation = new RegExp(/^\d*\.?\d*$/);
const validateNumeric = (value: string) => numericValidation.test(value);

const formFields: FC<Props> = ({
  index,
  width,
  fieldData,
  onChange,
  errorState,
  currentStep,
  formSharedValues,
  compressHeight,
  setSharedValue,
  locId,
  currentStepId,
  collectValues,
  personalizationPillsShown,
  handlePersonalizationShown,
  personalizationCompleted,
  stepInputState,
}: Props) => {
  const [value, setValue] = useState('');
  const { SSO } = useSelector((state: IRootState) => state);
  const pills = useSelector((store: any) => store.collegePill);

  useEffect(() => {
    if (!getDefaultValueForField(fieldData)) setValue('');
  }, [currentStep]);

  const change = (e: any) => {
    // store nessessary values to store//
    setSharedValue([
      {
        [e.target.fieldId]: e.target.value,
      },
    ]);
    setValue(e.target.value);
    if (onChange) onChange(e);
  };

  const handleChange = (e: any) => {
    const validatedInput = validateInput(e.target.value);
    if (validatedInput !== e.target.value) {
      const oldLength = e.target.value.length;
      const oldIndex = e.target.selectionStart;
      const newIndex = Math.max(
        0,
        validatedInput.length - oldLength + oldIndex
      );
      e.target.value = validatedInput;
      e.target.selectionStart = newIndex;
      e.target.selectionEnd = newIndex;
    }
    change(e);
  };

  const handleNumericValueChange = (e: any) => {
    const { originalEvent } = e;
    const isValidNumber = validateNumeric(originalEvent.target.value);

    if (isValidNumber) {
      change(e);
    } else originalEvent.target.value = ''; // Clear the value of the input if it's not a number
  };

  const displayHeaderCartoonBoolean = displayHeaderCartoon(currentStepId);

  const renderField = (field: FieldData) => {
    let type = field.inputType;
    const id = field.fieldId;
    field.value = SSO[field.fieldId] ? SSO[field.fieldId] : value;
    field.label = field.label.length > 1 ? field.label : `\u00a0`;

    const error = errorState[field.fieldId] || false;

    // Forces number input type to certain field ID's//
    mapToNumberInput.forEach(item => {
      if (id === item) type = 'number';
    });

    // Forces a validation type dependent on the name//
    const mapNameToValidationType = {
      postal: 'zip',
      sat: 'sat',
      act: 'act',
      gpa: 'gpa',
      birthdate: 'dob',
    };

    const mapNameToMinValue = (name: string) => {
      switch (name) {
        case 'sat':
          return '400';
        case 'act':
          return '1';
        case 'gpa':
          return '0.0';
        default:
          return undefined;
      }
    };

    const mapNameToMaxValue = (name: string) => {
      switch (name) {
        case 'sat':
          return '1600';
        case 'act':
          return '36';
        case 'gpa':
          return '4.0';
        default:
          return undefined;
      }
    };

    const mapNameToStepValue = (name: string) => {
      switch (name) {
        case 'sat':
          return '10';
        case 'act':
          return '1';
        case 'gpa':
          return '0.1';
        default:
          return undefined;
      }
    };

    // Set Validation
    const validationType = mapNameToValidationType[field.fieldId]
      ? mapNameToValidationType[field.fieldId]
      : field.inputType;

    // from https://eslint.org/docs/rules/no-param-reassign
    // The linter does not like assigning new values to existing objects.  This overrides that behaviour
    /* eslint no-param-reassign: ["error", { "props": false }] */

    if (SSO[field.fieldId]) {
      field.hasError = validateField(
        validationType,
        field.value,
        formSharedValues
      );
      collectValues && collectValues();
    } else {
      field.hasError = validateField(
        validationType,
        formSharedValues[field.fieldId],
        formSharedValues
      );
    }

    switch (type.toLocaleLowerCase()) {
      case 'buttonlist': {
        return (
          <ButtonList
            field={field}
            error={error}
            key={field.fieldId}
            onChange={handleChange}
          />
        );
      }
      case 'text': {
        return (
          <Input
            value={stepInputState[field.fieldId] || field.value}
            error={error}
            label={field.label}
            name={field.fieldId}
            key={field.fieldId}
            placeholder={field.placeholder}
            type={type}
            onChange={handleChange}
            autoCompleteType={getAutoCompleteKey(field.fieldId)}
            required={field.required}
          />
        );
      }
      case 'textaidisclaimer': {
        return (
          <InputAIDisclaimer
            value={stepInputState[field.fieldId] || field.value}
            error={error}
            label={field.label}
            name={field.fieldId}
            key={field.fieldId}
            placeholder={field.placeholder}
            type={type}
            onChange={handleChange}
            personalizationPillsShown={personalizationPillsShown}
            personalizationCompleted={personalizationCompleted}
          />
        );
      }
      case 'label': {
        return <Label text={field.label} key={field.fieldId} />;
      }
      case 'zip': {
        return (
          <Input
            error={error}
            label={field.label}
            name={field.fieldId}
            key={field.fieldId}
            placeholder={field.placeholder}
            type={type}
            onChange={handleChange}
            autoCompleteType={getAutoCompleteKey(field.fieldId)}
            required={field.required}
          />
        );
      }
      case 'number': {
        return (
          <Input
            value={stepInputState[field.fieldId] || value}
            error={error}
            label={field.label}
            name={field.fieldId}
            key={field.fieldId}
            placeholder={field.placeholder}
            type="number"
            onChange={handleNumericValueChange}
            autoCompleteType={getAutoCompleteKey(field.fieldId)}
            max={mapNameToMaxValue(field.fieldId)}
            min={mapNameToMinValue(field.fieldId)}
            step={mapNameToStepValue(field.fieldId)}
            required={field.required}
          />
        );
      }
      case 'radiolist':
      case 'radio': {
        return (
          <RadioButtons
            width={width}
            error={error}
            field={field}
            onChange={handleChange}
            checked={false}
            name={field.fieldId}
            key={field.fieldId}
            defaultValue={
              stepInputState[field.fieldId] || getDefaultValueForField(field)
            }
          />
        );
      }
      case 'list': {
        return (
          <SelectBox
            value={stepInputState[field.fieldId] || value}
            error={error}
            name={field.fieldId}
            key={field.fieldId}
            label={field.label}
            items={field.list}
            defaultValue={
              stepInputState[field.fieldId] || getDefaultValueForField(field)
            }
            placeholder={field.placeholder}
            onChange={handleChange}
          />
        );
      }
      case 'pilllist': {
        return <PillList pills={field} onChange={handleChange} />;
      }
      case 'personalizationtoggle': {
        return (
          <PillList
            pills={field}
            onChange={handleChange}
            isPersonalizationToggle={true}
            showPersonalizationPills={personalizationPillsShown}
            onCaretClick={handlePersonalizationShown}
            displayHeaderCartoonBoolean={displayHeaderCartoonBoolean}
            personalizationCompleted={personalizationCompleted}
          />
        );
      }
      case 'select': {
        return (
          <SelectBox
            value={
              stepInputState[field.fieldId] &&
              stepInputState[field.fieldId] !== 'undefined'
                ? stepInputState[field.fieldId]
                : value
            }
            error={error}
            name={field.fieldId}
            key={field.fieldId}
            label={field.label}
            items={field.list}
            defaultValue={
              stepInputState[field.fieldId] || getDefaultValueForField(field)
            }
            placeholder={field.placeholder}
            onChange={handleChange}
          />
        );
      }

      case 'asyncautocomplete': {
        return (
          <AsyncAutoComplete
            error={error}
            label={field.label}
            name={field.fieldId}
            key={field.fieldId}
            placeholder={field.placeholder}
            type={type}
            // @ts-ignore
            onChange={handleChange}
            locId={locId}
            value={stepInputState[field.fieldId]}
          />
        );
      }
      case 'asyncautocompletemultiselect': {
        return (
          <AsyncAutoComplete
            error={error}
            label={field.label}
            name={field.fieldId}
            key={field.fieldId}
            placeholder={field.placeholder}
            type={type}
            // @ts-ignore
            onChange={handleChange}
            locId={locId}
            isMultiSelect={field.fieldId === 'schools_interested'}
            hideInputBox={
              field.fieldId === 'schools_interested' && pills.length >= 10
            }
          />
        );
      }
      case 'autocomplete': {
        return (
          <AutoComplete
            error={error}
            name={field.fieldId}
            key={field.fieldId}
            label={field.label}
            items={field.list}
            placeholder={field.placeholder}
            defaultValue={
              stepInputState[field.fieldId] || getDefaultValueForField(field)
            }
            // @ts-ignore
            type={type}
            onChange={handleChange}
            allowDropdown={true}
          />
        );
      }
      case 'date': {
        return (
          <DateInput
            value={stepInputState[field.fieldId] || value}
            error={error}
            label={field.label}
            name={field.fieldId}
            key={field.fieldId}
            placeholder={field.placeholder}
            type={type}
            onChange={handleChange}
          />
        );
      }
      case 'phone': {
        return (
          <Input
            value={stepInputState[field.fieldId] || field.value}
            error={error}
            label={field.label}
            name={field.fieldId}
            key={field.fieldId}
            placeholder={field.placeholder}
            type="tel"
            onChange={handleChange}
            autoCompleteType="tel"
            required={field.required}
          />
        );
      }
      case 'email': {
        return (
          <Input
            value={stepInputState[field.fieldId] || field.value}
            error={error}
            label={field.label}
            name={field.fieldId}
            key={field.fieldId}
            placeholder={field.placeholder}
            type="email"
            onChange={handleChange}
            autoCompleteType="email"
            required={field.required}
          />
        );
      }
      case 'gdpr_consent_checkbox': {
        return (
          <CheckButton
            error={field.error}
            label={field.label}
            name={field.fieldId}
            key={field.fieldId}
            type="checkBox"
            onChange={handleChange}
            compressHeight
          >
            I have read and accept the YouVisit&nbsp;
            <a
              href="https://www.youvisit.com/terms-of-use/"
              target="_blank"
              rel="noopener noreferrer"
            >
              Terms of Use
            </a>
            &nbsp; and&nbsp;
            <a
              href="https://www.youvisit.com/privacy-policy/"
              target="_blank"
              rel="noopener noreferrer"
            >
              Privacy Policy
            </a>
            .
          </CheckButton>
        );
      }
      case 'marketing_consent_checkbox': {
        return (
          <CheckButton
            error={field.error}
            label={field.label}
            name={field.fieldId}
            key={field.fieldId}
            type="checkBox"
            onChange={handleChange}
          >
            I consent to receiving marketing materials.
          </CheckButton>
        );
      }
      case 'checkbox': {
        return (
          <CheckButton
            error={field.error}
            label={field.label}
            name={field.fieldId}
            key={field.fieldId}
            type="checkBox"
            onChange={handleChange}
          >
            {field.label}
          </CheckButton>
        );
      }
      default: {
        return <div>Type Not Found</div>;
      }
    }
  };
  return (
    <FormItem
      key={index}
      index={
        fieldData.fieldId === FORM_STATE.personalizationPreferences ? 2 : index
      } // need personalization preferences to have lower z-index than personalization suggestions
      zIndex={1000 - index}
      width={`${width}%`}
      compressHeight={compressHeight}
    >
      {renderField(fieldData)}
      {!compressHeight && (
        <FieldError
          show={errorState[fieldData.fieldId]}
          fieldId={fieldData.fieldId}
        />
      )}
    </FormItem>
  );
};

function mapStateToProps(state: any) {
  return {
    formSharedValues: state.formSharedValues,
    errorState: state.errorState,
    currentStep: state.currentStep,
  };
}

const mapDispatchToProps = (dispatch: any) => {
  return {
    setSharedValue: (fieldValue: any) => {
      const formattedFieldValues = fieldValue.map((stepObject: any) => {
        return {
          fieldKey: Object.keys(stepObject)[0],
          fieldValue: Object.values(stepObject)[0],
        };
      });
      dispatch(setSharedValueState(formattedFieldValues));
    },
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(formFields);
try {
    // @ts-ignore
    formFields.displayName = "formFields";
    // @ts-ignore
    formFields.__docgenInfo = { "description": "", "displayName": "formFields", "props": { "personalizationCompleted": { "defaultValue": null, "description": "", "name": "personalizationCompleted", "required": true, "type": { "name": "boolean" } }, "compressHeight": { "defaultValue": null, "description": "", "name": "compressHeight", "required": false, "type": { "name": "boolean" } }, "width": { "defaultValue": null, "description": "", "name": "width", "required": false, "type": { "name": "number" } }, "index": { "defaultValue": null, "description": "", "name": "index", "required": true, "type": { "name": "number" } }, "error": { "defaultValue": null, "description": "", "name": "error", "required": false, "type": { "name": "boolean" } }, "stepInputState": { "defaultValue": null, "description": "", "name": "stepInputState", "required": true, "type": { "name": "any" } }, "fieldData": { "defaultValue": null, "description": "", "name": "fieldData", "required": true, "type": { "name": "any" } }, "onChange": { "defaultValue": null, "description": "", "name": "onChange", "required": false, "type": { "name": "(e: any) => void" } }, "collectValues": { "defaultValue": null, "description": "", "name": "collectValues", "required": false, "type": { "name": "() => void" } }, "value": { "defaultValue": null, "description": "", "name": "value", "required": false, "type": { "name": "(e: any) => void" } }, "locId": { "defaultValue": null, "description": "", "name": "locId", "required": true, "type": { "name": "number" } }, "personalizationPillsShown": { "defaultValue": null, "description": "", "name": "personalizationPillsShown", "required": false, "type": { "name": "boolean" } }, "handlePersonalizationShown": { "defaultValue": null, "description": "", "name": "handlePersonalizationShown", "required": false, "type": { "name": "(input: boolean) => void" } }, "currentStepId": { "defaultValue": null, "description": "", "name": "currentStepId", "required": false, "type": { "name": "number" } } } };
    // @ts-ignore
    if (typeof STORYBOOK_REACT_CLASSES !== "undefined")
        // @ts-ignore
        STORYBOOK_REACT_CLASSES["src/containers/formFields/formFields.tsx#formFields"] = { docgenInfo: formFields.__docgenInfo, name: "formFields", path: "src/containers/formFields/formFields.tsx#formFields" };
}
catch (__react_docgen_typescript_loader_error) { }